/**
 * Delivery Status List Utilities
 * Helper to get list of available delivery statuses for autocomplete
 */

// List of available delivery statuses (Zeedcom format - keys from KOMMA_TO_OZONE_MAP)
export interface DeliveryStatusOption {
  key: string;
  label: string;
  color?: string;
}

// Delivery statuses mapped from KOMMA_TO_OZONE_MAP and OZONE_STATUS_MAP
export const DELIVERY_STATUSES: DeliveryStatusOption[] = [
  { key: 'paid', label: 'Payé', color: '#9ccc65' },
  { key: 'invoiced', label: 'Facturé', color: '#25a0d7' },
  { key: 'pending', label: 'Nouveau Colis', color: '#25a0d7' },
  { key: 'waiting_pickup', label: 'Attente De Ramassage', color: '#ffba57' },
  { key: 'picked_up', label: 'Ramassé', color: '#ffba57' },
  { key: 'sent', label: 'Expédié', color: '#0000ff' },
  { key: 'received', label: 'Reçu', color: '#9ccc65' },
  { key: 'in_distribution', label: 'Mise en distribution', color: '#25a0d7' },
  { key: 'in_progress', label: 'En cours', color: '#ffba57' },
  { key: 'returned', label: 'Retourné', color: '#eb1a00' },
  { key: 'delivered', label: 'Livré', color: '#9ccc65' },
  { key: 'postponed', label: 'Reporté', color: '#448aff' },
  { key: 'no_answer', label: 'Pas de réponse + SMS', color: '#25a0d7' },
  { key: 'unreachable', label: 'Injoignable', color: '#00bcd4' },
  { key: 'out_of_area', label: 'Hors-zone', color: '#00bcd4' },
  { key: 'canceled', label: 'Annulé', color: '#eb1a00' },
  { key: 'refused', label: 'Refusé', color: '#EB1A00' },
  { key: 'in_transit', label: 'En Voyage', color: '#400080' },
  { key: 'error', label: 'Erreur Numero', color: '#ff8080' },
  { key: 'interested', label: 'client intéressé', color: '#ff05d5' },
  { key: 'scheduled', label: 'Programmé', color: '#0c72b0' },
  { key: 'voicemail', label: 'Boite Vocal', color: '#00bcd4' },
  { key: 'no_answer_team', label: 'Pas de reponse ( SUIVI )', color: '#00bcd4' },
  { key: 'unreachable_team', label: 'Injoignable ( SUIVI )', color: '#00bcd4' },
  { key: 'voicemail_team', label: 'Boite Vocal ( SUIVI )', color: '#00bcd4' },
  { key: 'canceled_team', label: 'Annulé ( SUIVI )', color: '#00bcd4' },
  { key: 'postponed_team', label: 'Reporté ( SUIVI )', color: '#00bcd4' },
  { key: 'not_paid', label: 'Non Payé', color: '#25a0d7' },
  { key: 'postponed_today', label: 'reporté aujourd hui', color: '#ffe438' },
  { key: 'no_address', label: 'sans adresse', color: '#ff0000' },
  { key: 'no_answer_displacement', label: 'Pas de réponse (déplacement)', color: '#25a0d7' },
  { key: 'refunded', label: 'Remboursé', color: '#9ccc65' },
  { key: 'sent_to_agency', label: 'Envoyé à l\'agence', color: '#25a0d7' },
  { key: 'received_in_agency', label: 'Reçu à l\'agence', color: '#9ccc65' },
  { key: 'no_answer_day_2', label: 'Pas de réponse jour 2', color: '#25a0d7' },
  { key: 'no_answer_day_3', label: 'Pas de réponse jour 3', color: '#25a0d7' },
  { key: 'no_answer_displacement_day_2', label: 'Pas de réponse (déplacement) jour 2', color: '#25a0d7' },
  { key: 'no_answer_displacement_day_3', label: 'Pas de réponse (déplacement) jour 3', color: '#25a0d7' },
  { key: 'seized', label: 'Saisi', color: '#eb1a00' },
  { key: 'pre_picked_up', label: 'Pré-ramassé', color: '#ffba57' },
  { key: 'damaged', label: 'Endommagé', color: '#ec3609' },
  { key: 'delivery_delay_48_72h', label: 'Retard Livraison 48h-72h', color: '#d97517' },
  { key: 'out_of_sector', label: 'Hors Secteur', color: '#000000' },
  { key: 'uncovered_zone', label: 'Zone Non-couverte', color: '#000000' }
];

/**
 * Get delivery status by key
 */
export const getDeliveryStatusByKey = (key: string): DeliveryStatusOption | undefined => {
  return DELIVERY_STATUSES.find(status => status.key === key);
};

/**
 * Get all delivery status keys
 */
export const getDeliveryStatusKeys = (): string[] => {
  return DELIVERY_STATUSES.map(status => status.key);
};

/** Liste { value, label } pour filtres / selects, triée par libellé. */
export const getDeliveryStatusSelectOptions = (): Array<{ value: string; label: string }> => {
  return [...DELIVERY_STATUSES]
    .map(s => ({ value: s.key, label: s.label }))
    .sort((a, b) => a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' }));
};

/**
 * Canonique + valeurs présentes en base/API mais absentes du référentiel (intégrations, anciennes données).
 */
export const mergeDeliveryStatusOptionsFromApi = (
  fromApi: Array<{ value: string; label: string }> | undefined | null
): Array<{ value: string; label: string }> => {
  const map = new Map<string, { value: string; label: string }>();
  getDeliveryStatusSelectOptions().forEach(o => map.set(o.value, o));
  (fromApi || []).forEach(o => {
    if (!o?.value) return;
    if (!map.has(o.value)) {
      map.set(o.value, { value: o.value, label: o.label || o.value });
    }
  });
  return Array.from(map.values()).sort((a, b) =>
    a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' })
  );
};
